import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Accordion,
} from "react-bootstrap";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import useClientTableSort, {
  sortRowsByConfig,
} from "../../hooks/useClientTableSort";
import useAccess from "../../hooks/useAccess";

const IncidentList = () => {
  const [records, setRecords] = useState([]);
  const [recordsUnfiltered, setRecordsUnfiltered] = useState([]);
  const [filterAccidentType, setFilterAccidentType] = useState(null);
  const [filterAccidentNo, setFilterAccidentNo] = useState("");
  const [filterDotStatus, setFilterDotStatus] = useState(null);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterSavedBy, setFilterSavedBy] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const tableRef = useRef(null);
  const itemsPerPage = 20;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const { hasPermission } = useAccess();
  const userId = isAdmin && paramId ? paramId : localStorage.getItem("userId");
  const canDeleteRecords = isAdmin && hasPermission("Task", "Task", "delete");

  const accidentTypeOptions = [
    { label: "Accident", value: "Accident" },
    { label: "Claim", value: "Claim" },
    { label: "Hazmat Incident", value: "Hazmat Incident" },
    { label: "Other", value: "Other" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const res = await axios.get(`${API_BASE}/api/incidents/${userId}`, {
          headers,
        });
        setRecordsUnfiltered(res.data);
        setRecords(res.data);
      } catch (err) {
        console.error("Fetch error", err);
      }
    };
    fetchData();
  }, [API_BASE, userId]);

  const applyFilters = () => {
    let filtered = [...recordsUnfiltered];

    if (filterAccidentType?.length) {
      const selectedTypes = filterAccidentType.map((t) => t.value);
      filtered = filtered.filter((i) => selectedTypes.includes(i.inc_type));
    }

    if (filterAccidentNo) {
      filtered = filtered.filter((i) =>
        i.inc_number
          ?.toString()
          .toLowerCase()
          .includes(filterAccidentNo.toLowerCase())
      );
    }

    if (filterDotStatus) {
      filtered = filtered.filter(
        (i) =>
          (filterDotStatus === "DOT" && i.inc_dot_reportable === "DOT") ||
          (filterDotStatus === "non-DOT" && i.inc_dot_reportable !== "DOT")
      );
    }

    if (filterDateFrom) {
      filtered = filtered.filter(
        (i) => new Date(i.inc_date) >= new Date(filterDateFrom)
      );
    }

    if (filterDateTo) {
      filtered = filtered.filter(
        (i) => new Date(i.inc_date) <= new Date(filterDateTo)
      );
    }

    if (filterSavedBy?.length) {
      const selectedUsers = filterSavedBy.map((s) => s.value);
      filtered = filtered.filter((i) =>
        selectedUsers.includes(i.saved_by_name)
      );
    }

    setRecords(filtered);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterAccidentType(null);
    setFilterAccidentNo("");
    setFilterDotStatus(null);
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterSavedBy(null);
    setRecords(recordsUnfiltered);
  };

  const sortConfig = useClientTableSort(tableRef, () => setCurrentPage(1));
  const sortedRecords = sortRowsByConfig(records, sortConfig, [
    (record) => record.reviewed,
    (record) => record.inc_preventable,
    (record) => record.inc_dot_reportable,
    (record) => record.inc_type,
    (record) => record.inc_number,
    (record) => record.inc_date,
    (record) => record.inc_saved_date,
    (record) => record.saved_by_name || "-",
  ]);

  const paginatedRecords = sortedRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const savedByOptions = [
    ...new Set(recordsUnfiltered.map((i) => i.saved_by_name).filter(Boolean)),
  ].map((s) => ({ label: s, value: s }));

  const handleCancel = () => {
    if (isAdmin && paramId) {
      navigate(`/userhome/${paramId}`);
    } else {
      navigate("/userhome");
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!canDeleteRecords || !recordId) return;
    if (!window.confirm("Delete this record? This cannot be undone.")) return;

    try {
      await axios.delete(`${API_BASE}/api/incidents/${recordId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords((prev) => prev.filter((row) => row.inc_id !== recordId));
      setRecordsUnfiltered((prev) => prev.filter((row) => row.inc_id !== recordId));
    } catch (err) {
      console.error("Incident delete failed", err);
      alert(err?.response?.data?.message || "Failed to delete record.");
    }
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Incidents</h3>
        <div>
          <Button variant="secondary" className="me-2" onClick={handleCancel}>
            Return
          </Button>
          <Button
            variant="success"
            onClick={() => navigate(`/incidents/${userId}/incident-new`)}
          >
            Add
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          <Accordion defaultActiveKey="">
            <Accordion.Item eventKey="0">
              <Accordion.Header>Filters</Accordion.Header>
              <Accordion.Body>
                <Form className="mb-4 nw-filter-panel">
                  <Row className="align-items-end g-3 nw-filter-grid">
                    <Col md={3}>
                      <Form.Label>Type of Accident</Form.Label>
                      <Select
                        isMulti
                        options={accidentTypeOptions}
                        value={filterAccidentType}
                        onChange={setFilterAccidentType}
                        placeholder="Select Accident Type(s)"
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Accident No.</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search Accident Number"
                        value={filterAccidentNo}
                        onChange={(e) => setFilterAccidentNo(e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>DOT Status</Form.Label>
                      <div>
                        <Form.Check
                          inline
                          type="radio"
                          label="DOT"
                          name="dotStatus"
                          checked={filterDotStatus === "DOT"}
                          onChange={() => setFilterDotStatus("DOT")}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          label="non-DOT"
                          name="dotStatus"
                          checked={filterDotStatus === "non-DOT"}
                          onChange={() => setFilterDotStatus("non-DOT")}
                        />
                      </div>
                    </Col>
                    <Col md={2}>
                      <Form.Label>Date From</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Date To</Form.Label>
                      <Form.Control
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Saved By</Form.Label>
                      <Select
                        isMulti
                        options={savedByOptions}
                        value={filterSavedBy}
                        onChange={setFilterSavedBy}
                        placeholder="Select Saved By"
                      />
                    </Col>
                    <Col md={3} className="d-flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={clearFilters}
                        className="mt-3"
                      >
                        Clear Filter
                      </Button>
                      <Button
                        variant="success"
                        onClick={applyFilters}
                        className="mt-3"
                      >
                        Filter
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {records.length === 0 ? (
            <p className="mt-3 text-muted fst-italic">No incidents found.</p>
          ) : (
            <div className="table-responsive mt-3">
              <table
                ref={tableRef}
                className="table table-bordered align-middle text-center"
                data-nw-client-sort="true"
              >
                <thead className="table-light">
                  <tr>
                    <th>Reviewed</th>
                    <th>Preventable</th>
                    <th>DOT</th>
                    <th>Type Of Incident</th>
                    <th>Incident No.</th>
                    <th>Incident Date</th>
                    <th>Date Saved</th>
                    <th>Saved By</th>
                    <th className="text-center" style={{ width: "110px" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((i) => (
                    <tr key={i.inc_id}>
                      <td className="text-center">{i.reviewed}</td>
                      <td className="text-center">{i.inc_preventable}</td>
                      <td className="text-center">{i.inc_dot_reportable}</td>
                      <td className="text-center">{i.inc_type}</td>
                      <td className="text-center">{i.inc_number}</td>
                      <td className="text-center">
                        {i.inc_date
                          ? new Date(i.inc_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-center">
                        {i.inc_saved_date
                          ? new Date(i.inc_saved_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-center">{i.saved_by_name || "-"}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            data-print-record-path={`/incidents/${userId}/incident-detail/${i.inc_id}`}
                            onClick={() =>
                              navigate(
                                `/incidents/${userId}/incident-detail/${i.inc_id}`
                              )
                            }
                          >
                            Edit
                          </Button>
                          {canDeleteRecords && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteRecord(i.inc_id)}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                currentPage={currentPage}
                totalItems={records.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default IncidentList;
